import {
  LivestreamStatus,
  LivestreamVisibility,
  type LivestreamAudienceFilters,
  type LivestreamSource,
  type ExternalRestreamTarget,
  type RestreamTargetRuntimeStatus,
} from '@masters-club/types';
import type { SegmentDefinition } from '@masters-club/validation';

/**
 * DTO админского API трансляций. Соответствует AdminLivestreamDTO в
 * apps/backend/src/domains/livestreams/livestream.service.ts.
 *
 * После миграции 0068+:
 *  - Аудитория задаётся через `audienceFilters` (jsonb) — BroadcastFilters-shape.
 *  - Локализация (title/description) — массив `translations[]`, один на язык.
 *  - `languages[]` — список языков, на которых эфир анонсируется/транслируется.
 *  - `thumbnailFileId` — опциональная обложка-превью (File record).
 */
export interface LivestreamTranslation {
  languageCode: string;
  title: string;
  description: string | null;
}

export interface Livestream {
  id: number;
  /** Primary title — дубликат translations[0].title, оставлен для fallback-отображения. */
  title: string;
  description: string | null;
  visibility: LivestreamVisibility;
  status: LivestreamStatus;
  hostAdminId: number;
  streamKey: string;
  /** Активный источник входящего потока (OBS/Zoom), отдаваемый зрителям. */
  activeSource: LivestreamSource;
  chatRoomId: number | null;
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  recordingEnabled: boolean;
  recordingFileId: number | null;
  thumbnailFileId: number | null;
  peakViewers: number;
  totalUniqueViewers: number;
  maxViewersOverride: number | null;
  cancelReason: string | null;
  testMode: boolean;
  createdAt: string;
  updatedAt: string;
  audienceFilters: LivestreamAudienceFilters | SegmentDefinition | null;
  languages: string[];
  translations: LivestreamTranslation[];
  linkedModuleItemId: number | null;
  linkedModuleId: string | null;
  linkedCourseId: string | null;
  /** Внешние площадки рестрима (RTMP). streamKey каждой — секрет. */
  externalRestreamTargets: ExternalRestreamTarget[];
  /** Runtime-статусы relay per-target (live). Пусто = relay не запущен. */
  relayStatuses: RestreamTargetRuntimeStatus[];
  /** Публичный URL страницы плеера на платформе. Используется в Zoom Custom Live Stream. */
  watchPageUrl: string;
}

export type { LivestreamAudienceFilters, SegmentDefinition, ExternalRestreamTarget, RestreamTargetRuntimeStatus };

/** Человекочитаемые метки площадок рестрима. */
export const RESTREAM_PLATFORM_LABELS: Record<ExternalRestreamTarget['platform'], string> = {
  YOUTUBE: 'YouTube',
  FACEBOOK: 'Facebook',
  VK: 'VK Видео',
  OK: 'Одноклассники',
  CUSTOM: 'Произвольный RTMP',
};

/** Подсказка ingest-URL по площадке (заполняется в форме как плейсхолдер). */
export const RESTREAM_PLATFORM_URL_HINTS: Record<ExternalRestreamTarget['platform'], string> = {
  YOUTUBE: 'rtmps://a.rtmps.youtube.com/live2',
  FACEBOOK: 'rtmps://live-api-s.facebook.com:443/rtmp/',
  VK: 'rtmps://ovsu.mycdn.me/input/',
  OK: 'rtmp://vsu.mycdn.me/input/',
  CUSTOM: 'rtmp://…',
};

/** Метки и цвета runtime-статусов relay для Badge. */
export const RELAY_STATUS_META: Record<
  RestreamTargetRuntimeStatus['status'],
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  IDLE: { label: 'Ожидание', variant: 'secondary' },
  CONNECTING: { label: 'Подключение', variant: 'outline' },
  LIVE: { label: 'В эфире', variant: 'default' },
  ERROR: { label: 'Ошибка', variant: 'destructive' },
  STOPPED: { label: 'Остановлен', variant: 'secondary' },
};

/**
 * Статус одного источника входящего потока. Зеркало SourceStatusDTO бэкенда
 * (apps/backend/src/domains/livestreams/livestream-source.service.ts).
 */
export interface SourceStatus {
  source: LivestreamSource;
  /** Publish-key для OBS/Zoom (`<key>` или `<key>-zoom`). */
  publishKey: string;
  /** Публикует ли сейчас живой поток. */
  publishing: boolean;
  /** Активен ли (отдаётся зрителям). */
  active: boolean;
  readyTime: string | null;
  resolution: { width: number; height: number } | null;
  bitrateMbps: number | null;
}

/** Зеркало LivestreamSourcesDTO бэкенда. Ответ GET /admin/livestreams/:id/sources. */
export interface LivestreamSourcesStatus {
  livestreamId: number;
  status: LivestreamStatus;
  activeSource: LivestreamSource;
  sources: SourceStatus[];
  snapshotAt: string;
}

/**
 * Человекочитаемые метки и цвета статусов эфира. Используются в list/show.
 */
export const LIVESTREAM_STATUS_META: Record<LivestreamStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; dotClass: string }> = {
  [LivestreamStatus.SCHEDULED]: { label: 'Запланирована', variant: 'secondary', dotClass: 'bg-gray-400' },
  [LivestreamStatus.LIVE]: { label: 'В эфире', variant: 'destructive', dotClass: 'bg-red-500 animate-pulse' },
  [LivestreamStatus.RECONNECTING]: { label: 'Переподключение', variant: 'outline', dotClass: 'bg-yellow-500' },
  [LivestreamStatus.ENDED]: { label: 'Завершена', variant: 'default', dotClass: 'bg-sky-500' },
  [LivestreamStatus.CANCELLED]: { label: 'Отменена', variant: 'outline', dotClass: 'bg-zinc-400' },
};

export const LIVESTREAM_VISIBILITY_LABELS: Record<LivestreamVisibility, string> = {
  [LivestreamVisibility.PUBLIC]: 'Публичная',
  [LivestreamVisibility.PRIVATE]: 'Приватная',
};

/**
 * RTMP URL для OBS. Берётся из VITE_RTMP_URL build-time env;
 * в деве падает на localhost, на проде указывается в deploy.sh --build-arg.
 */
export const DEFAULT_RTMP_URL = import.meta.env.VITE_RTMP_URL ?? 'rtmp://localhost:1935/live';

/**
 * Состояние формы create/edit. Маппится в payload при submit.
 */
export interface LivestreamFormData {
  visibility: LivestreamVisibility;
  scheduledAt: string | null;
  recordingEnabled: boolean;
  maxViewersOverride: number | null;
  thumbnailFileId: number | null;
  languages: string[];
  translations: LivestreamTranslation[];
  audienceFilters: SegmentDefinition | null;
  linkedModuleItemId: number | null;
  /** Площадки рестрима редактируются на отдельном табе show, не в общей форме. */
  externalRestreamTargets?: ExternalRestreamTarget[];
}

/**
 * Helper — вернуть заголовок эфира в предпочтительном языке с fallback.
 * Используется в list и show.
 */
export function getLivestreamTitle(
  livestream: Pick<Livestream, 'title' | 'translations'>,
  preferredLanguage = 'ru',
): { title: string; isFallback: boolean } {
  const translations = livestream.translations ?? [];
  const preferred = translations.find((t) => t.languageCode === preferredLanguage);
  if (preferred) {
    return { title: preferred.title, isFallback: false };
  }
  if (translations.length > 0) {
    return { title: translations[0].title, isFallback: true };
  }
  return { title: livestream.title, isFallback: false };
}

export function getLivestreamDescription(
  livestream: Pick<Livestream, 'description' | 'translations'>,
  preferredLanguage = 'ru',
): string | null {
  const translations = livestream.translations ?? [];
  const preferred = translations.find((t) => t.languageCode === preferredLanguage);
  if (preferred) {
    return preferred.description ?? null;
  }
  if (translations.length > 0) {
    return translations[0].description ?? null;
  }
  return livestream.description;
}
